import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { CC_ERROR_TEXT, CC_PANEL } from "@/components/case-checking/caseCheckingChrome";
import {
  computedGradeFromRuleOverrides,
  rulesChangedAfterOverallSignoff,
} from "@/components/case-checking/overallAssessmentGrade";
import { ReviewOverallSignoffCard } from "@/components/case-checking/ReviewOverallSignoffCard";
import { useProblemStatementRun } from "@/components/case-checking/useProblemStatementRun";
import { useHumanRuleReviews } from "@/components/case-checking/useHumanRuleReviews";
import type { CaseCheckingGrade } from "@/components/case-checking/caseCheckingStatusBadges";
import { finalOutcomeId } from "@/components/case-checking/outcomeCatalog";
import {
  patchProblemStatementHumanReviews,
  type CaseReview as ApiCaseReview,
  type ProblemStatementHumanReviewPatch,
} from "@/services/api/case-checking";

export function ReviewOverallAssessmentStep({
  caseId,
  caseReview,
  fallbackGrade,
  onAssessmentComplete,
  onAssessmentDirtyChange,
  companyId,
}: {
  caseId: string;
  caseReview: ApiCaseReview | null;
  fallbackGrade: CaseCheckingGrade;
  /** Called when final-outcome human review is saved or already present on load. */
  onAssessmentComplete?: () => void;
  /** True until overall grade is saved, or while edits are unsaved. */
  onAssessmentDirtyChange?: (dirty: boolean) => void;
  companyId?: string;
}) {
  const overallStatementId = finalOutcomeId(companyId);
  const { run, loading, error, scorable, setRun } = useProblemStatementRun(caseId);
  const { overridesByKey, reviewRun } = useHumanRuleReviews(caseId);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const suggestedGrade = useMemo(
    () => computedGradeFromRuleOverrides(scorable, overridesByKey),
    [scorable, overridesByKey],
  );

  const savedReview = useMemo(() => {
    const reviews = run?.human_reviews ?? [];
    return reviews.find((r) => r.problem_statement_id === overallStatementId);
  }, [overallStatementId, run?.human_reviews]);

  const rulesChangedSinceSignoff = useMemo(
    () => rulesChangedAfterOverallSignoff(savedReview?.reviewed_at, reviewRun?.reviews ?? []),
    [reviewRun?.reviews, savedReview?.reviewed_at],
  );

  useEffect(() => {
    if (savedReview?.reviewed_at) onAssessmentComplete?.();
  }, [savedReview?.reviewed_at, onAssessmentComplete]);

  const saveOverall = useCallback(
    async (patch: ProblemStatementHumanReviewPatch) => {
      if (!run) return;
      setSaveState("saving");
      setSaveError(null);
      try {
        const updated = await patchProblemStatementHumanReviews(caseId, run.id, [patch]);
        setRun(updated);
        setSaveState("saved");
        onAssessmentComplete?.();
        window.setTimeout(() => setSaveState("idle"), 2000);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : "Failed to save");
        setSaveState("idle");
      }
    },
    [caseId, onAssessmentComplete, run, setRun],
  );

  if (loading) {
    return <p className="text-sm text-slate-400">Loading assessment…</p>;
  }

  if (error || !run) {
    return (
      <div className={CC_PANEL}>
        <p className="text-sm text-slate-600 dark:text-slate-300">{error ?? "No assessment available yet."}</p>
      </div>
    );
  }

  return (
    <div data-testid="review-assessment-step">
      {saveError ? <p className={clsx("mb-2 text-xs", CC_ERROR_TEXT)}>{saveError}</p> : null}
      <ReviewOverallSignoffCard
        caseReview={caseReview}
        fallbackGrade={fallbackGrade}
        savedReview={savedReview}
        onSave={(patch) => void saveOverall(patch)}
        saveState={saveState}
        suggestedGrade={suggestedGrade}
        rulesChangedSinceSignoff={rulesChangedSinceSignoff}
        companyId={companyId}
        onDirtyChange={onAssessmentDirtyChange}
      />
    </div>
  );
}
